import { DatePipe } from '@angular/common';
import { Moment } from 'moment';
import momentBusinessDay from 'moment-business-days';
import * as moment from 'moment-timezone';

export default class DateHelper {
  private static datepipe: DatePipe = new DatePipe('nl-BE');

  public static readonly DEFAULT_TIME_ZONE = 'Europe/Brussels';

  public static readonly DEFAULT_LOCALE = 'nl-BE';

  public static readonly DEFAULT_MOMENT_FORMATS = {
    parse: {
      dateInput: 'LL',
    },
    display: {
      dateInput: 'DD-MM-YYYY',
      monthYearLabel: 'MMM YYYY',
      dateA11yLabel: 'LL',
      monthYearA11yLabel: 'MMMM YYYY',
    },
  };

  public static readonly DEFAULT_MIN_BUSINESS_DAYS_FOR_PROJECT_PLACEMENT: number = 4;

  private static MOMENT_BUSINESS_DAYS_LOCALE_SET = false;

  static compare(d1: Date, d2: Date): number {
    if (d1 == null && d2 == null) {
      return 0;
    }
    if (d1 == null) {
      return -1;
    }
    if (d2 == null) {
      return 1;
    }
    return d1.getTime() - d2.getTime();
  }

  static equal(d1: Date, d2: Date): boolean {
    return this.compare(d1, d2) === 0;
  }

  static min(d1: Date, d2: Date): Date {
    if (d1 == null && d2 == null) {
      return null;
    }
    if (d1 == null) {
      return d2;
    }
    if (d2 == null) {
      return d1;
    }
    return d1 < d2 ? d1 : d2;
  }

  static max(d1: Date, d2: Date): Date {
    if (d1 == null && d2 == null) {
      return null;
    }
    if (d1 == null) {
      return d2;
    }
    if (d2 == null) {
      return d1;
    }
    return d1 < d2 ? d2 : d1;
  }

  static parseDate(
    toParse: any,
    timeZone = 'Europe/Brussels',
    locale = 'nl-BE'
  ): Date {
    return moment(toParse).tz(timeZone).locale(locale).toDate();
  }

  static formatDate(date: Date, format?: string) {
    return this.datepipe.transform(
      date,
      format == null ? 'dd/MM/yyyy' : format
    );
  }

  static formatDateTime(date: Date) {
    let toReturn = this.formatDate(date);
    return date == null
      ? toReturn
      : toReturn + ' ' + moment(date).tz('Europe/Brussels').format('HH:mm');
  }

  static formatTimeOnly(date: Date): string {
    return moment(date).tz('Europe/Brussels').format('HH:mm');
  }

  static getMoment(timeZone = 'Europe/Brussels', locale = 'nl-BE'): Moment {
    return moment.tz(timeZone).locale(locale);
  }

  static getMomentAtStartOfDay(
    timeZone = 'Europe/Brussels',
    locale = 'nl-BE'
  ): Moment {
    return this.getMoment(timeZone, locale).startOf('day');
  }

  static getMomentAtEndOfDay(
    timeZone = 'Europe/Brussels',
    locale = 'nl-BE'
  ): Moment {
    return this.getMoment(timeZone, locale).endOf('day');
  }

  static parseMoment(
    toParse: any,
    timeZone = this.DEFAULT_TIME_ZONE,
    locale = this.DEFAULT_LOCALE
  ): Moment {
    return moment(toParse).tz(timeZone).locale(locale);
  }

  static getMomentBusinessDayRelativeToToday(diff: number): Moment {
    if (!this.MOMENT_BUSINESS_DAYS_LOCALE_SET) {
      momentBusinessDay.updateLocale('nl-be', {});
      this.MOMENT_BUSINESS_DAYS_LOCALE_SET = true;
    }
    return momentBusinessDay().businessAdd(diff).startOf('day');
  }

  static getMomentBusinessDayMinProjectPlacement(): Moment {
    return this.getMomentBusinessDayRelativeToToday(
      this.DEFAULT_MIN_BUSINESS_DAYS_FOR_PROJECT_PLACEMENT
    );
  }

  static calculateCalendarWeekDiff(a: Moment, b: Moment): number {
    return moment(a).startOf('week').diff(moment(b).startOf('week'), 'weeks');
  }
}
